import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles, ScrollText, Heart, MessageCircleQuestion, Moon, CloudMoon,
  ArrowRight, Orbit, Stars,
} from "lucide-react";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import CosmicBackground from "../components/astro/CosmicBackground";
import PanchangCard from "../components/astro/PanchangCard";
import { useLang } from "../i18n/LanguageContext";
import { useBooking } from "../context/BookingContext";

const TOOLS = [
  { to: "/astrology/kundali", icon: Orbit, title: "AI Birth Chart Analysis",
    desc: "Enter your birth details and receive a full personalised reading of your Lagna, planets, houses and Dasha periods.",
    span: "sm:col-span-2 lg:col-span-2 lg:row-span-2", tone: "vermilion", cta: "Start my analysis" },
  { to: "/astrology/kundali", icon: ScrollText, title: "Kundali Generator",
    desc: "A complete Vedic Kundali from your name, date, exact time and place of birth.", tone: "teal", cta: "Generate Kundali" },
  { to: "/astrology/matching", icon: Heart, title: "Kundali Matching",
    desc: "36-point Guna Milan with emotional, communication, financial and family compatibility.", tone: "vermilion", cta: "Check compatibility" },
  { to: "/astrology/ask", icon: MessageCircleQuestion, title: "Ask the Astrology AI",
    desc: "Career, marriage, love, study, money, family or spiritual life — ask in your own words.", tone: "teal", cta: "Ask a question" },
  { to: "/astrology/panchang", icon: Moon, title: "Daily Panchang",
    desc: "Tithi, Nakshatra, Yoga, Karana, sunrise, sunset, Rahu Kaal and Abhijit Muhurta, updated live.", tone: "teal", cta: "See today" },
  { to: "/astrology/dream", icon: CloudMoon, title: "Dream Interpretation",
    desc: "Describe a dream and receive its traditional symbolism and spiritual guidance.", tone: "vermilion", cta: "Interpret my dream" },
];

const JOURNEY = ["Enter birth details", "Generate Kundali", "AI analysis", "Explore life areas",
                 "Dasha timeline", "Traditional insights", "Ask the AI", "Book a consultation"];

export default function Astrology() {
  const { t } = useLang();
  const { openBooking } = useBooking();

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "AI Vedic Astrology & Kundali Analysis",
    provider: { "@type": "Person", name: "Shivam Chatterjee" },
    areaServed: "Worldwide",
    description:
      "AI-assisted Vedic astrology readings computed with the Swiss Ephemeris (Lahiri ayanamsa): birth chart analysis, Kundali generation, Guna Milan matching, Vimshottari Dasha, daily Panchang and dream interpretation.",
  };

  return (
    <div className="relative" data-testid="astrology-page">
      <Seo
        title="AI Vedic Astrology, Kundali & Panchang"
        description="Free AI-powered Vedic astrology readings: birth chart analysis, complete Kundali, 36-point Guna Milan matching, Vimshottari Dasha timeline, daily Panchang and dream interpretation — computed with the Swiss Ephemeris and interpreted in the Tarapith tradition."
        path="/astrology"
        keywords={["AI astrology", "free Kundali", "Vedic birth chart analysis", "Guna Milan", "daily Panchang", "Vimshottari Dasha", "Tarapith astrologer"]}
        jsonLd={jsonLd}
      />
      <CosmicBackground dense />

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20 sm:pt-20">
        <div className="max-w-3xl">
          <motion.span
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="badge-gold mb-7 inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[11px] font-serif uppercase tracking-[0.28em]"
          >
            <Stars className="h-3.5 w-3.5" /> Ancient wisdom · modern intelligence
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.75, delay: 0.08 }}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight mb-6"
          >
            <span className="text-gradient-flame">Discover What the Stars Reveal About Your Life</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.16 }}
            className="text-base md:text-lg text-tara-body leading-relaxed mb-9 max-w-2xl"
          >
            Get personalized AI-powered Vedic Astrology insights based on your birth details, Kundali,
            planetary positions, and traditional astrological principles.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.24 }}
            className="flex flex-wrap items-center gap-4"
          >
            <Link to="/astrology/kundali" className="btn-sacred aura-pulse flex items-center gap-2.5 rounded-full px-8 py-4 font-serif font-semibold" data-testid="astro-cta-free-reading">
              <Sparkles className="h-5 w-5" /> Get Your Free Astrology Reading
            </Link>
            <Link to="/astrology/kundali" className="btn-ghost-gold flex items-center gap-2.5 rounded-full px-7 py-4 font-serif font-semibold" data-testid="astro-cta-kundali">
              <ScrollText className="h-5 w-5" /> Generate My Kundali
            </Link>
            <Link to="/astrology/ask" className="flex items-center gap-2 font-serif font-semibold text-tara-teal hover:gap-3 transition-all" data-testid="astro-cta-ask">
              <MessageCircleQuestion className="h-5 w-5" /> Ask the Astrology AI
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
            className="mt-10 flex flex-wrap gap-x-7 gap-y-3 text-xs text-tara-muted"
          >
            <span>Swiss Ephemeris · Lahiri ayanamsa</span>
            <span>·</span><span>Sidereal, not sun-sign horoscopes</span>
            <span>·</span><span>English · বাংলা · हिन्दी</span>
          </motion.div>
        </div>
      </section>

      {/* TOOLS — bento */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20" data-testid="astro-tools">
        <Reveal variant="blur" className="max-w-xl mb-10">
          <span className="text-[11px] font-serif tracking-[0.28em] uppercase text-tara-tealDeep">Astrology tools</span>
          <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-tara-ink mt-3">Everything your chart can tell you</h2>
          <div className="rule-gold mt-5" />
        </Reveal>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4 auto-rows-[minmax(150px,auto)]">
          {TOOLS.map((tool, i) => (
            <Reveal key={tool.title} variant="up" delay={(i % 4) * 0.06} className={`h-full ${tool.span || ""}`}>
              <Link
                to={tool.to}
                className="glass bento-tile frame-gold group flex h-full w-full flex-col rounded-[1.5rem] p-6"
                data-testid={`astro-tool-${tool.title.split(" ")[0].toLowerCase()}`}
              >
                <span className={`mb-5 flex h-12 w-12 items-center justify-center rounded-2xl ${
                  tool.tone === "vermilion"
                    ? "bg-gradient-to-br from-tara-sindoor to-tara-vermilion"
                    : "bg-gradient-to-br from-tara-tealDeep to-tara-teal"
                }`}>
                  <tool.icon className="h-5 w-5 text-white" />
                </span>
                <h3 className={`font-display font-semibold text-tara-ink leading-snug mb-2 ${tool.span ? "text-3xl" : "text-xl"}`}>
                  {tool.title}
                </h3>
                <p className="text-sm text-tara-body leading-relaxed">{tool.desc}</p>
                <span className="mt-auto pt-5 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-tara-teal group-hover:gap-3 transition-all">
                  {tool.cta} <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PANCHANG + JOURNEY */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 grid lg:grid-cols-[1.1fr_1fr] gap-6">
        <Reveal variant="scale"><PanchangCard /></Reveal>
        <Reveal variant="up" delay={0.1} className="glass-strong rounded-[1.5rem] p-7">
          <span className="text-[11px] font-serif tracking-[0.28em] uppercase text-tara-tealDeep">Your journey</span>
          <h3 className="font-serif text-2xl font-semibold text-tara-ink mt-3 mb-6">From birth details to real guidance</h3>
          <ol className="space-y-3">
            {JOURNEY.map((step, i) => (
              <li key={step} className="flex items-center gap-3" data-testid={`astro-journey-${i}`}>
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-tara-teal/30 text-[11px] font-bold text-tara-tealDeep">
                  {i + 1}
                </span>
                <span className="text-sm text-tara-body">{step}</span>
              </li>
            ))}
          </ol>
          <button
            onClick={() => openBooking("kundali-reading")}
            className="btn-sacred mt-7 flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-serif font-semibold"
            data-testid="astro-book-consultation"
          >
            🙏 {t("cta_speak")}
          </button>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="glass rounded-[1.5rem] p-6">
          <p className="text-xs leading-relaxed text-tara-muted">
            <strong className="text-tara-sindoor">A gentle note:</strong> these readings are traditional Vedic
            interpretations offered for reflection and spiritual guidance. They are not predictions, and never a
            substitute for qualified medical, legal, mental-health or financial advice. No specific outcome is guaranteed.
          </p>
        </div>
      </section>
    </div>
  );
}
